import axios from 'axios';
import ENV from "../../../environment";
import { toast } from "react-toastify";
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import useFetch from '../../../hooks/useFetch';
import ConfirmDeletion from './ConfirmDeletion';
import { IoIosSave } from "react-icons/io";

// Icons
import ImageCard from './ImageCard';
import { FiUpload } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";

// Components
import ImageDropZone from './ImageDropZone';
import WarningContainer from './WarningContainer';
import ImageViewer from "./ImageViewer";
import ImageCroper from "./ImageCroper";
import ImageCardSkeleton from './ImageCardSkeleton';

function Indexe() {
    const { hotelid } = useParams();
    const [hotelImages, setHotelImages] = useState([]);
    const [thisItem, setThisItem] = useState(null);
    const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
    const [isImageCroperModalOpen, setIsImageCroperModalOpen] = useState(false);
    const [isImageViewerModalOpen, setIsImageViewerModalOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [fileUploadLoading, setFileUploadLoading] = useState(false);

    const headers = {
        Authorization: sessionStorage.getItem("token"),
        "Content-Type": "application/png",
        "client-id": ENV.CLIENTID,
        "client-secret": ENV.SECRET,
    };

    // REQUESTS
    const { data: deleteRequestData, loading: deleteImageLoading, error: deleteImageRequestError, fetchData: deleteRequest } = useFetch(`${ENV.URL}/api/hdp/tts/adm/hotel/image/${thisItem?.uuid}/${hotelid}`, { headers, method: "DELETE" });
    const { data: hotelImagesRequestData, error: hotelImagesRequestError, loading: hotelImagesLoading, fetchData: getHotelImagesRequest } = useFetch(`${ENV.URL}/api/hdp/tts/adm/hotel/images-uuids/${hotelid}`, { headers });
    const { data: updatingOrderData, loading: updateOrderLoading, error: updatingOrderDataError, fetchData: updateImagesOrderRequest } = useFetch(`${ENV.URL}/api/hdp/tts/adm/hotel/image-order/${hotelid}`, {
        ...headers,
        method: "PUT",
        body: hotelImages.map((image, index) => {
            return {
                uuid: image.uuid,
                order: ++index
            }
        })
    });

    // Error handling
    useEffect(() => {
        if (deleteImageRequestError) {
            setIsDeletionModalOpen(false);
            toast.error(deleteImageRequestError[0]?.message || "An error occurred");
        } else if (hotelImagesRequestError) {
            toast.error(hotelImagesRequestError[0]?.message || "An error occurred");
        } else if (updatingOrderDataError) {
            toast.error(updatingOrderDataError[0]?.message || "An error occurred");
        }
    }, [deleteImageRequestError, hotelImagesRequestError, updatingOrderDataError])

    useEffect(() => {
        if (updatingOrderData) {
            setIsChanged(false);
            toast.success("Successfully reordered")
            getHotelImagesRequest();
        }
    }, [updatingOrderData]);

    useEffect(() => {
        if (deleteRequestData) {
            getHotelImagesRequest();
            setIsDeletionModalOpen(false);
        }
    }, [deleteRequestData])

    const getImage = async () => {
        for (let i = 0; i < hotelImages.length; i++) {
            fetch(hotelImages[i].uri, { method: "GET", headers })
                .then((res) => res.blob())
                .then((blob) => {
                    if (/image/.test(blob.type) && document.querySelector("#c-" + hotelImages[i]?.uuid))
                        document.querySelector("#c-" + hotelImages[i]?.uuid).src = window.URL.createObjectURL(blob);
                });
        }
    };

    useEffect(() => {
        getImage();
    }, [hotelImages]);

    function base64ToFile(base64Data, filename) {
        const byteCharacters = atob(base64Data.split(',')[1]);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset++) {
            const byte = byteCharacters.charCodeAt(offset);
            byteArrays.push(byte);
        }

        const blob = new Blob([new Uint8Array(byteArrays)], { type: 'image/jpeg' });
        const file = new File([blob], filename, { type: 'image/jpeg' });

        return file;
    }

    const handleImageUpload = async (croppedImageUrl) => {
        if (!croppedImageUrl || hotelImages.length >= 5) return;
        const croppedFile = base64ToFile(croppedImageUrl, file?.name);
        const formData = new FormData();
        formData.append("file", croppedFile);
        setFileUploadLoading(true);
        await axios
            .post(`${ENV.URL}/api/hdp/tts/adm/hotel/image/${hotelid}`, formData, {
                headers: {
                    authorization: "Bearer " + sessionStorage.getItem("token"),
                    "Content-Type": "multipart/form-data",
                    "client-id": ENV.CLIENTID,
                    "client-secret": ENV.SECRET,
                },
            })
            .then(() => {
                setIsImageCroperModalOpen(false);
                setThisItem(null);
                toast.success("Saved successfully", { autoClose: 2000 });
            })
            .catch((err) => {
                setFile(null);
                toast.error(err?.response?.data?.errors[0]?.message || "An error occurred");
            }).finally(() => {
                setFile(null);
                setFileUploadLoading(false);
                getHotelImagesRequest();
            });
    }

    useEffect(() => {
        if (hotelImagesRequestData) {
            const updatedHotelImagesList = Array.from(hotelImagesRequestData, image => ({
                ...image,
                uri: `${ENV.URL}/api/hdp/tts/adm/hotel/image/${image.uuid}/${hotelid}`
            }));

            setHotelImages(updatedHotelImagesList);
            setImages(updatedHotelImagesList)
        }
    }, [hotelImagesRequestData]);

    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const [images, setImages] = useState([]);
    const [isChanged, setIsChanged] = useState(false);

    const handleSort = () => {
        let _hotelImages = [...hotelImages];
        const draggedItemContent = _hotelImages[dragItem.current];
        _hotelImages.splice(dragItem.current, 1);
        _hotelImages.splice(dragOverItem.current, 0, draggedItemContent);
        dragItem.current = null;
        dragOverItem.current = null;
        setHotelImages(_hotelImages);
    }

    const checkOrderChanged = () => {
        if (!hotelImages.length) return;
    
        const isNotSorted = hotelImages.some((image, index) => {
            const nextImage = hotelImages[index + 1];
            if (!nextImage) return false;
            return image.order > nextImage.order;
        });
    
        setIsChanged(isNotSorted);
    };
    

    useEffect(() => {
        checkOrderChanged();
    }, [hotelImages]);

    const handleCancelChanges = () => {
        setHotelImages(images);
        setIsChanged(false);
    }

    useEffect(() => {
        if (file)
            setIsImageCroperModalOpen(true);
    }, [file]);

    useEffect(() => {
        getHotelImagesRequest();
    }, []);

    const styles = {
        page: {
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            marginTop: "16px",
            marginBottom: "16px",
        },
        header: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            text: {
                fontSize: "20px",
                lineHeight: "28px",
                fontWeight: "600",
                margin: "0",
            },
            button: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--mainColor)",
                color: "white",
                gap: "8px",
                padding: "8px 16px",
                fontWeight: "500",
                fontSize: "14px",
                lineHeight: "20px",
                borderRadius: "6px",
                cursor: "pointer",
                outline: "none",
                border: "1px solid #E4E4E7",
                icon: {
                    width: "18px",
                    height: "18px"
                }
            }
        },
        imagesContainer: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: "16px",
            marginTop: "24px",
            marginBottom: isChanged ? "80px" : "0px"
        },
        saveChangesBox: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderTop: "1px solid #E4E4E7",
            backgroundColor: "white",
            padding: "16px",
            position: "fixed",
            bottom: "0",
            gap: "16px",
            left: "0",
            heigth: "30px",
            width: "100%",
            button: {
                padding: "8px 16px",
                fontWeight: "500",
                fontSize: "14px",
                lineHeight: "20px",
                borderRadius: "6px",
                cursor: "pointer",
                outline: "none",
                border: "1px solid #E4E4E7",
                backgroundColor: "#fff"
            },
            saveBtn: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--mainColor)",
                color: "white",
                gap: "8px",
                icon: {
                    width: "18px",
                    height: "18px"
                }
            }
        }
    }

    return (
        <>
            {
                isDeletionModalOpen && <ConfirmDeletion image={thisItem} loading={deleteImageLoading} onCancel={() => {
                    setThisItem(null);
                    setIsDeletionModalOpen(false);
                }}
                    onDelete={deleteRequest}
                />
            }
            {
                isImageViewerModalOpen && <ImageViewer image={thisItem} onCancel={() => {
                    setThisItem(null);
                    setIsImageViewerModalOpen(false);
                }} />
            }
            {
                isImageCroperModalOpen && <ImageCroper image={file} onCancel={() => {
                    setThisItem(null);
                    setIsImageCroperModalOpen(false);
                }}
                    onComplete={handleImageUpload}
                    loading={fileUploadLoading}
                />
            }
            <div style={styles.page} className="container">
                <div style={styles.header}>
                    <h2 style={styles.header.text}>Hotel Images</h2>
                    <button disabled={hotelImages.length >= 5} style={styles.header.button} onClick={() => document.getElementById("image-input").click()}                    >
                        <FiUpload style={styles.header.button.icon} />
                        Upload Image
                    </button>
                    <input onChange={(e) => setFile(e.target.files[0])} hidden accept="image/*" type="file" id="image-input" />
                </div>
                {
                    hotelImages.length < 5 && <WarningContainer>
                        Uploading all 5 images will significantly improve the experience for booking users. You have uploaded {hotelImages.length}/5 images.
                    </WarningContainer>
                }
                <WarningContainer>
                    <>
                        <MdDragIndicator style={{
                            width: "18px",
                            height: "18px",
                            marginRight: "4px",
                            color: "#3b82f6",
                        }} />
                        Drag and drop images to reorder. The first image will be displayed as the primary image.
                    </>
                </WarningContainer>
                <div style={styles.imagesContainer}>
                    {
                        hotelImages.length > 0 && !hotelImagesLoading && hotelImages.map((image, index) => (
                            <ImageCard key={index} isPrimary={index === 0}
                                image={image}
                                onDragStart={() => dragItem.current = index}
                                onDragEnter={() => dragOverItem.current = index}
                                onDragEnd={handleSort}
                                onDragOver={(e) => e.preventDefault()}
                                openDeletionModal={() => {
                                    setThisItem(image);
                                    setIsDeletionModalOpen(true);
                                }}
                                openImageViewerModal={() => {
                                    setThisItem(image);
                                    setIsImageViewerModalOpen(true);
                                }}
                            />
                        ))
                    }
                    {
                        hotelImagesLoading && Array.from({ length: 5 }, (_, i) => {
                            return <ImageCardSkeleton key={i} />
                        })
                    }
                    {
                        hotelImages.length < 5 && <ImageDropZone onClick={() => document.getElementById("image-input").click()} />
                    }
                </div>
                {
                    isChanged && <div style={styles.saveChangesBox}>
                        <button disabled={updateOrderLoading} onClick={handleCancelChanges} style={styles.saveChangesBox.button}>Cancel</button>
                        <button disabled={updateOrderLoading} onClick={updateImagesOrderRequest} style={{ ...styles.saveChangesBox.button, ...styles.saveChangesBox.saveBtn }}>
                            <IoIosSave style={styles.saveChangesBox.saveBtn.icon} />
                            Save Changes
                        </button>
                    </div>
                }
            </div>
        </>
    )
}

export default Indexe;